import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PublishIcon from "@mui/icons-material/Publish";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import {
  SALES_COLUMN_COMMON_FIELDS,
  SALES_COLUMN_FIELDS_BY_TYPE,
  SALES_DISPLAY_ON_DEFAULT,
  SALES_DISPLAY_ON_OPTIONS,
  SALES_SECTION_ORDER_STEP,
  SALES_SECTION_REGISTRY,
  SALES_SECTION_TYPE_OPTIONS,
  mainLayoutStyles,
  normalizeSalesDisplayOn,
  showNotification,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import SalesFieldRenderer from "../../components/SalesFieldRenderer/SalesFieldRenderer";
import ItemsEditor from "../../components/ItemsEditor/ItemsEditor";
import { buildDefaultColumn } from "../../components/ColumnEditor/ColumnEditor";
import {
  createSalesSection,
  getSalesPageSections,
  getSalesPageById,
  getPublishedSectionById,
  isPublishPending,
  publishSection,
  getSalesSectionById,
  updateSalesSection,
} from "../../hooks/SalesPagesHook";

// Treat empty rich-text HTML (e.g. "<p></p>") as blank for required checks.
const isFieldEmpty = (field, value) => {
  if (field.type === "boolean") return false; // booleans are never "required-empty"
  const str = String(value ?? "");
  if (field.type === "richtext") {
    return str.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim() === "";
  }
  return str.trim() === "";
};

const DEFAULT_SECTION_TYPE = SALES_SECTION_TYPE_OPTIONS[0].value;

// Seed a new section's content from any `default` values declared on the
// section type's fields (e.g. lottie loop/autoplay = true, imageGrid columns = 3).
const buildDefaultContent = (type) => {
  const reg = SALES_SECTION_REGISTRY[type];
  const out = {};
  (reg?.fields || []).forEach((f) => {
    if (f.type === "column") {
      // Seed a column object with its declared type's defaults.
      out[f.key] = buildDefaultColumn(f.default?.type || "richText");
    } else if (f.default !== undefined) {
      out[f.key] = f.default;
    }
  });
  return out;
};

// Validate a nested column object (twoColumn). Returns an error string or null.
const validateColumn = (columnLabel, column) => {
  const col = column || {};
  const typeFields = SALES_COLUMN_FIELDS_BY_TYPE[col.type] || [];
  for (const field of typeFields) {
    if (field.required && isFieldEmpty(field, col[field.key])) {
      return `${columnLabel}: "${field.label}" is required`;
    }
  }
  return null;
};

// Serialize a column object, keeping only fields relevant to its type.
const cleanColumn = (column) => {
  const col = column || {};
  const type = col.type || "richText";
  const out = { type };
  const keep = (field) => {
    const v = col[field.key];
    if (v !== undefined && v !== "") out[field.key] = v;
  };
  SALES_COLUMN_COMMON_FIELDS.forEach(keep);
  (SALES_COLUMN_FIELDS_BY_TYPE[type] || []).forEach(keep);
  return out;
};

const SalesSectionEditContainer = () => {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const isEdit = !!id;

  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sectionType, setSectionType] = useState(DEFAULT_SECTION_TYPE);
  const [order, setOrder] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [displayOn, setDisplayOn] = useState(SALES_DISPLAY_ON_DEFAULT);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(() =>
    buildDefaultContent(DEFAULT_SECTION_TYPE)
  );
  const [pageLabel, setPageLabel] = useState(pageId);
  // Denormalized slug stored on each section for the public consumer's query.
  const [pageSlug, setPageSlug] = useState("");
  const [publishing, setPublishing] = useState(false);
  // Draft/published records for this section, used to gate the Publish button.
  const [draftRecord, setDraftRecord] = useState(null);
  const [publishedRecord, setPublishedRecord] = useState(null);

  const publishPending =
    isEdit && isPublishPending(draftRecord, publishedRecord);

  const registry = SALES_SECTION_REGISTRY[sectionType];

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const loadPage = async () => {
    try {
      const page = await getSalesPageById(pageId);
      setPageLabel(page?.title || pageId);
      setPageSlug(page?.slug || "");
    } catch (error) {
      setPageLabel(pageId);
    }
  };

  useEffect(() => {
    if (isEdit) loadSection();
    else initNewOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSection = async () => {
    try {
      setLoading(true);
      const data = await getSalesSectionById(id);
      if (!data) {
        showNotification("Section not found");
        navigate(-1);
        return;
      }
      setSectionType(data.section || DEFAULT_SECTION_TYPE);
      setOrder(data.order ?? "");
      setEnabled(data.enabled !== false);
      setDisplayOn(normalizeSalesDisplayOn(data.displayOn));
      setTitle(data.title || "");
      setContent(data.content || {});
      setDraftRecord(data);
      const published = await getPublishedSectionById(id);
      setPublishedRecord(published);
    } catch (error) {
      console.log("Error loading section:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const initNewOrder = async () => {
    try {
      const sections = await getSalesPageSections(pageId);
      const maxOrder = sections.reduce(
        (max, s) => Math.max(max, s.order || 0),
        0
      );
      setOrder(maxOrder + SALES_SECTION_ORDER_STEP);
    } catch (error) {
      setOrder(SALES_SECTION_ORDER_STEP);
    }
  };

  const setContentField = (key, value) =>
    setContent((prev) => ({ ...prev, [key]: value }));

  // Reset content (to the new type's defaults) when the type changes while
  // adding a new section.
  const handleTypeChange = (value) => {
    setSectionType(value);
    if (!isEdit) setContent(buildDefaultContent(value));
  };

  const validate = () => {
    if (order === "" || Number.isNaN(Number(order))) {
      showNotification("Please enter a valid order number");
      return false;
    }
    for (const field of registry.fields) {
      if (field.type === "column") {
        const err = validateColumn(field.label, content[field.key]);
        if (err) {
          showNotification(err);
          return false;
        }
        continue;
      }
      if (field.required && isFieldEmpty(field, content[field.key])) {
        showNotification(`"${field.label}" is required`);
        return false;
      }
    }
    if (registry.hasItems) {
      const items = content.items || [];
      if (items.length === 0) {
        showNotification("Add at least one item");
        return false;
      }
      for (let i = 0; i < items.length; i++) {
        for (const field of registry.itemFields) {
          if (field.required && isFieldEmpty(field, items[i][field.key])) {
            showNotification(
              `Item ${i + 1}: "${field.label}" is required`
            );
            return false;
          }
        }
      }
    }
    return true;
  };

  const buildContent = () => {
    // Keep only fields defined by the registry, plus normalized items[] order.
    const out = {};
    registry.fields.forEach((f) => {
      if (f.type === "column") {
        out[f.key] = cleanColumn(content[f.key]);
        return;
      }
      const v = content[f.key];
      if (v !== undefined && v !== "") out[f.key] = v;
    });
    if (registry.hasItems) {
      out.items = (content.items || []).map((item, index) => {
        const cleaned = { order: index + 1 };
        registry.itemFields.forEach((f) => {
          const v = item[f.key];
          if (v !== undefined && v !== "") cleaned[f.key] = v;
        });
        return cleaned;
      });
    }
    return out;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const payload = {
        pageId,
        page: pageSlug,
        section: sectionType,
        order: Number(order),
        enabled,
        displayOn: normalizeSalesDisplayOn(displayOn),
        title: title.trim(),
        content: buildContent(),
      };
      if (isEdit) {
        await updateSalesSection(id, payload, userId);
        showNotification("Section updated");
      } else {
        await createSalesSection(payload, userId);
        showNotification("Section created");
      }
      navigate(`/sales-pages/content/${pageId}`);
    } catch (error) {
      console.log("Error saving section:", error.message);
      showNotification("Error saving section");
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(`/sales-pages/content/${pageId}`);

  const handlePublish = async () => {
    try {
      setPublishing(true);
      await publishSection(id, userId);
      showNotification("Section published");
      await loadSection();
    } catch (error) {
      console.log("Error publishing section:", error.message);
      showNotification("Error publishing section");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`${isEdit ? "Edit" : "Add"} Section — ${pageLabel}`}
        bgColor="#fff"
        onBackClick={handleCancel}
        editEnabled
        cancelButton
        onCancelClick={handleCancel}
        submitButtonText="Save"
        handleSave={handleSave}
        loading={saving}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          <Paper sx={{ p: 3, maxWidth: 760 }}>
            {isEdit ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <Chip
                  size="small"
                  color={publishPending ? "warning" : "success"}
                  variant={publishPending ? "filled" : "outlined"}
                  label={
                    publishPending
                      ? publishedRecord
                        ? "Unpublished changes"
                        : "Not published"
                      : "Published"
                  }
                />
                <Tooltip
                  title={
                    publishPending
                      ? "Publish this section to the live site"
                      : "Nothing to publish — save a change first"
                  }
                >
                  <span>
                    <LoadingButton
                      variant="contained"
                      startIcon={<PublishIcon />}
                      loading={publishing}
                      disabled={!publishPending}
                      onClick={handlePublish}
                      style={publishButtonStyles}
                    >
                      Publish
                    </LoadingButton>
                  </span>
                </Tooltip>
              </Box>
            ) : null}

            {/* Common fields */}
            <Typography sx={labelStyles}>Section Type</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={sectionType}
              disabled={isEdit}
              onChange={(e) => handleTypeChange(e.target.value)}
              sx={{ mb: 1 }}
            >
              {SALES_SECTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={helperStyles}>{registry.description}</Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Typography sx={labelStyles}>Order</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <Tooltip title="Which viewports this section appears on (mobile ≤ 900px)">
                  <Typography sx={labelStyles}>Display On</Typography>
                </Tooltip>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={displayOn}
                  onChange={(e) => setDisplayOn(e.target.value)}
                >
                  {SALES_DISPLAY_ON_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4} sx={{ display: "flex", alignItems: "flex-end" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                  }
                  label="Enabled"
                />
              </Grid>
            </Grid>

            <Typography sx={labelStyles}>Internal Title (admin only)</Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box sx={dividerStyles} />

            {/* Dynamic content fields */}
            {registry.fields.map((field) => (
              <SalesFieldRenderer
                key={field.key}
                field={field}
                value={content[field.key]}
                page={pageSlug || pageId}
                onChange={(v) => setContentField(field.key, v)}
              />
            ))}

            {registry.hasItems ? (
              <ItemsEditor
                itemFields={registry.itemFields}
                items={content.items}
                page={pageSlug || pageId}
                onChange={(items) => setContentField("items", items)}
              />
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SalesSectionEditContainer;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 2,
};

const dividerStyles = {
  height: "1px",
  background: "#E0E0E0",
  my: 2,
};

const publishButtonStyles = {
  textTransform: "none",
  background: "#2E7D32",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
};
